import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { setOn401Handler } from "../api/client";

export type AuthUser = {
  id: number;
  email: string;
  is_admin: boolean;
};

type AuthState =
  | { status: "loading"; user: null }
  | { status: "authenticated"; user: AuthUser }
  | { status: "anonymous"; user: null };

type AuthContextValue = AuthState & {
  login: (email: string, password: string, rememberMe: boolean) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

async function fetchMe(): Promise<AuthUser | null> {
  const res = await fetch("/api/auth/me", { credentials: "same-origin" });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`/api/auth/me failed: ${res.status}`);
  return (await res.json()) as AuthUser;
}

export class LoginError extends Error {}

async function postLogin(
  email: string,
  password: string,
  rememberMe: boolean,
): Promise<AuthUser> {
  const res = await fetch("/api/auth/login", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password, remember_me: rememberMe }),
  });
  if (res.status === 401) {
    throw new LoginError("неверный email или пароль");
  }
  if (!res.ok) {
    throw new LoginError(`не удалось войти (${res.status})`);
  }
  return (await res.json()) as AuthUser;
}

async function postLogout(): Promise<void> {
  await fetch("/api/auth/logout", {
    method: "POST",
    credentials: "same-origin",
  });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading", user: null });
  const queryClient = useQueryClient();

  useEffect(() => {
    let alive = true;
    fetchMe()
      .then((user) => {
        if (!alive) return;
        if (user) setState({ status: "authenticated", user });
        else setState({ status: "anonymous", user: null });
      })
      .catch(() => {
        if (alive) setState({ status: "anonymous", user: null });
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    setOn401Handler(() => {
      setState({ status: "anonymous", user: null });
      queryClient.clear();
    });
    return () => setOn401Handler(null);
  }, [queryClient]);

  const login = useCallback(
    async (email: string, password: string, rememberMe: boolean) => {
      const user = await postLogin(email, password, rememberMe);
      setState({ status: "authenticated", user });
    },
    [],
  );

  const logout = useCallback(async () => {
    await postLogout();
    setState({ status: "anonymous", user: null });
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, logout }),
    [state, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
